import { useCallback, useMemo, useState } from 'react';
import type { SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import { useDeleteSongPublishMutation, useUpdateSongPublishMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';
import { localReleaseStart, publishAtOfValue, PublishTimingValue } from '../../promote/publish/PublishTimingField';
import { isLaterTooSoon, SongUploadError, songUploadErrorOf, songUploadTimings } from '../songUpload';

/**
 * "Change the time" and "Cancel the upload" of a waiting upload (design c6): `PATCH` and `DELETE
 * /accounts/:accountId/song-publishes/:uuid`. A cancel puts the row back to "Not linked".
 */
export function useSongUploadStatusActions({
  accountId,
  record,
  releaseDate,
  onCanceled,
}: {
  accountId: number;
  record: SerializedSongPublish;
  releaseDate: string | null;
  onCanceled: () => void;
}) {
  const timeZone = useArtistTimeZone();
  const [isEditing, setIsEditing] = useState(false);
  const [timing, setTiming] = useState<PublishTimingValue>({ timing: record.timing, pickedAt: '' });
  const [localError, setLocalError] = useState<SongUploadError | null>(null);
  const [update, updateState] = useUpdateSongPublishMutation();
  const [remove, removeState] = useDeleteSongPublishMutation();
  const timings = useMemo(
    () => songUploadTimings(localReleaseStart(releaseDate, timeZone), new Date()),
    [releaseDate, timeZone]
  );
  const publishAt = timing.timing === 'now' ? null : publishAtOfValue(timing, releaseDate, timeZone);

  const saveTime = useCallback(async () => {
    if (timing.timing === 'later' && isLaterTooSoon(publishAt, new Date())) {
      setLocalError('tooSoon');
      return;
    }
    setLocalError(null);
    const result = await update({
      accountId,
      catalogItemUuid: record.catalogItemUuid,
      publishUuid: record.uuid,
      body: { timing: timing.timing, publishAt },
    });
    if (isSuccessfulResult(result)) {
      setIsEditing(false);
    }
  }, [accountId, publishAt, record.catalogItemUuid, record.uuid, timing.timing, update]);

  const cancelUpload = useCallback(async () => {
    const result = await remove({ accountId, catalogItemUuid: record.catalogItemUuid, publishUuid: record.uuid });
    if (isSuccessfulResult(result)) {
      onCanceled();
    }
  }, [accountId, onCanceled, record.catalogItemUuid, record.uuid, remove]);

  const serverError = updateState.error ?? removeState.error;
  let error: SongUploadError | null = localError;
  if (error === null && serverError !== undefined) {
    error = songUploadErrorOf(serverError);
  }
  return {
    isEditing,
    startEditing: () => setIsEditing(true),
    timing,
    setTiming,
    timings,
    saveTime,
    cancelUpload,
    isBusy: updateState.isLoading || removeState.isLoading,
    error,
  };
}
